import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

export default function WorkEntryForm({ entry, onSuccess, onCancel, isAdmin = false }) {
  const [formData, setFormData] = useState({
    customer_name: '',
    job_description: '',
    date: '',
    day: '',
    start_time: '',
    stop_time: '',
    hours_worked: '',
    work_completed: '',
    work_type: 'remote',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (entry) {
      setFormData({
        customer_name: entry.customer_name || '',
        job_description: entry.job_description || '',
        date: entry.date || '',
        day: entry.day || '',
        start_time: entry.start_time || '',
        stop_time: entry.stop_time || '',
        hours_worked: entry.hours_worked || '',
        work_completed: entry.work_completed || '',
        work_type: entry.work_type || 'remote',
      });
    }
  }, [entry]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Auto-fill day when date is selected
    if (name === 'date' && value) {
      const dateObj = new Date(value);
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      setFormData((prev) => ({ ...prev, day: days[dateObj.getDay()] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        hours_worked: parseFloat(formData.hours_worked),
      };

      if (entry) {
        // Use admin endpoint if admin mode
        const endpoint = isAdmin ? `/admin/entries/${entry.id}` : `/entries/${entry.id}`;
        await axios.put(`${API}${endpoint}`, data);
        toast.success('Entry updated!');
      } else {
        await axios.post(`${API}/entries`, data);
        toast.success('Entry created!');
      }

      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="retro-panel">
      <h2 className="text-3xl font-bold mb-6 glow-text" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif' }}>
        {entry ? '[EDIT ENTRY]' : '[NEW ENTRY]'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Customer Name:
            </label>
            <input
              type="text"
              name="customer_name"
              data-testid="entry-customer-input"
              value={formData.customer_name}
              onChange={handleChange}
              className="retro-input w-full"
              required
            />
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Date:
            </label>
            <input
              type="date"
              name="date"
              data-testid="entry-date-input"
              value={formData.date}
              onChange={handleChange}
              className="retro-input w-full"
              required
            />
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Day:
            </label>
            <input
              type="text"
              name="day"
              data-testid="entry-day-input"
              value={formData.day}
              onChange={handleChange}
              className="retro-input w-full"
              required
              readOnly
            />
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Start Time:
            </label>
            <input
              type="time"
              name="start_time"
              data-testid="entry-starttime-input"
              value={formData.start_time}
              onChange={handleChange}
              className="retro-input w-full"
              required
            />
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Stop Time:
            </label>
            <input
              type="time"
              name="stop_time"
              data-testid="entry-stoptime-input"
              value={formData.stop_time}
              onChange={handleChange}
              className="retro-input w-full"
              required
            />
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Hours Worked:
            </label>
            <input
              type="number"
              name="hours_worked"
              data-testid="entry-hours-input"
              value={formData.hours_worked}
              onChange={handleChange}
              className="retro-input w-full"
              step="0.5"
              min="0"
              required
            />
          </div>

          <div>
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Work Type:
            </label>
            <select
              name="work_type"
              data-testid="entry-worktype-select"
              value={formData.work_type}
              onChange={handleChange}
              className="retro-input w-full"
              required
            >
              <option value="remote">REMOTE</option>
              <option value="on-site">ON-SITE</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
            Job Description:
          </label>
          <textarea
            name="job_description"
            data-testid="entry-jobdesc-textarea"
            value={formData.job_description}
            onChange={handleChange}
            className="retro-input w-full"
            rows="3"
            required
          />
        </div>

        <div>
          <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
            Work Completed:
          </label>
          <textarea
            name="work_completed"
            data-testid="entry-workcompleted-textarea"
            value={formData.work_completed}
            onChange={handleChange}
            className="retro-input w-full"
            rows="4"
            required
          />
        </div>

        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            data-testid="entry-submit-button"
            className="retro-btn retro-btn-green flex-1"
            disabled={loading}
          >
            {loading ? '[PROCESSING...]' : entry ? '[UPDATE]' : '[CREATE]'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            data-testid="entry-cancel-button"
            className="retro-btn retro-btn-magenta flex-1"
          >
            [CANCEL]
          </button>
        </div>
      </form>
    </div>
  );
}